import React from "react";
import EvidenceListView, { ListRow } from "./EvidenceListView";

const fyLabel = (id, financialYears) => financialYears.find((fy) => String(fy.id) === String(id))?.financial_year_value || "—";

/** Sub-location is the real entity when set (e.g. South Delhi); the parent
 *  location (Delhi) is only the aggregating record otherwise. */
const locationLabel = (doc, sources) => {
  const src = sources.find((s) => String(s.id) === String(doc?.sourceId));
  if (!src) return "—";
  const base = src.unitCode ? src.unitCode : `${src.location?.city} - ${src.location?.state}`;
  const sub = (src.subLocation || []).find((s) => String(s.id) === String(doc?.subLocationId));
  return sub ? `${base} / ${sub.subLocation}` : base;
};

/** KPI name(s) for a document/group, resolved off its own kpiIds (pre-review
 *  quantitative evidence) or questionId (once tied to a specific answer) —
 *  no extra per-row fetch, just a lookup into the bulk reportingQuestionsMap. */
const kpiLabel = (doc, reportingQuestionsMap) => {
  if (!doc) return "—";
  const ids = doc.questionId != null ? [doc.questionId] : (doc.kpiIds || []);
  const names = ids.map((id) => reportingQuestionsMap[id]?.title ?? reportingQuestionsMap[id]?.kpi_name).filter(Boolean);
  if (!names.length) return "—";
  return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2} more` : names.join(", ");
};

/** Day + month only — the Financial Year column already carries the year. */
const fmtDay = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
  catch { return d; }
};

const periodLabel = (doc) => {
  const from = fmtDay(doc?.answerFromDate);
  const to = fmtDay(doc?.answerToDate);
  if (!from || !to) return "—";
  return `${from} – ${to}`;
};

const COLUMNS = [
  { key: "name", label: "Name", flex: 2 },
  { key: "fy", label: "Financial Year", width: 110 },
  { key: "location", label: "Location", width: 140 },
  { key: "uploadedBy", label: "Uploaded By", width: 130 },
  { key: "answeredBy", label: "Answered By", width: 130 },
  { key: "period", label: "Period", width: 120 },
  { key: "analysis", label: "Analysis", width: 110 },
  { key: "verification", label: "Verification", width: 150 },
  { key: "chevron", label: "", width: 30 },
];

const EvidencesListView = ({ groups, sources, financialYears, reportingQuestionsMap, isLoading, error, onRetry, onRowClick }) => (
  <EvidenceListView
    columns={COLUMNS}
    rows={groups}
    rowKey={(g) => g.key}
    isLoading={isLoading}
    error={error}
    onRetry={onRetry}
    emptyTitle="No evidence found"
    emptySub="Evidence appears here once a document is linked to an answer, or once at least one KPI has been reviewed and saved from a batch."
    footer={<span>Showing {groups.length} entr{groups.length === 1 ? "y" : "ies"}</span>}
    renderRow={(group, index) => {
      const first = group.documents[0];
      const unverifiedCount = group.documentCount - group.verifiedCount;
      return (
        <ListRow index={index} onClick={() => onRowClick(group)}>
          <div style={{ flex: 2, padding: "0 10px", minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={group.label}>
              {group.label}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={kpiLabel(first, reportingQuestionsMap)}>
              {kpiLabel(first, reportingQuestionsMap)}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{group.documentCount.toLocaleString("en-IN")} file{group.documentCount === 1 ? "" : "s"}</div>
          </div>
          <div style={{ width: 110, padding: "0 10px", fontSize: 12, color: "#475569" }}>{fyLabel(first?.financialYearId, financialYears)}</div>
          <div style={{ width: 140, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {locationLabel(first, sources)}
          </div>
          <div style={{ width: 130, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {first?.uploadedByName || "—"}
          </div>
          <div style={{ width: 130, padding: "0 10px", fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {first?.answeredByName || "—"}
          </div>
          <div style={{ width: 120, padding: "0 10px", fontSize: 11.5, color: "#94a3b8" }}>{periodLabel(first)}</div>
          <div style={{ width: 110, padding: "0 10px" }}>
            {group.parsedCount > 0 ? (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1", whiteSpace: "nowrap" }}>
                {group.parsedCount.toLocaleString("en-IN")} parsed
              </span>
            ) : (
              <span style={{ fontSize: 11, color: "#cbd5e1" }}>—</span>
            )}
          </div>
          <div style={{ width: 150, padding: "0 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {group.verifiedCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#dcfce7", color: "#166534", whiteSpace: "nowrap" }}>
                <i className="fas fa-check-circle" style={{ marginRight: 4 }} />{group.verifiedCount.toLocaleString("en-IN")} verified
              </span>
            )}
            {unverifiedCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: "#fff7ed", color: "#c2410c", whiteSpace: "nowrap" }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: 4 }} />{unverifiedCount.toLocaleString("en-IN")} unverified
              </span>
            )}
          </div>
          <div style={{ width: 30, display: "flex", justifyContent: "center" }}>
            <i className="fas fa-chevron-right" style={{ fontSize: 11, color: "#cbd5e1" }} />
          </div>
        </ListRow>
      );
    }}
  />
);

export default EvidencesListView;
